/* eslint-disable func-style */
// --- Detection ---

import { Any } from '@common/defs/types';

export function isFacebookInAppBrowser(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || navigator.vendor || (window as Any).opera;
  return /FBAN|FBAV|Instagram/i.test(ua);
}

// Checks if storage is actually writable, not just present.
export function isLocalStorageAvailable(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const testKey = '__ls_test__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    return true;
  } catch (_error) {
    console.warn(`[SafeStorage] Blocked writing __ls_test__`, _error);
    return false;
  }
}

export function isFacebookWithBrokenStorage(): boolean {
  return isFacebookInAppBrowser() && !isLocalStorageAvailable();
}

// --- Safe Wrappers ---

interface SafeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

function createSafeStorage(type: 'localStorage' | 'sessionStorage'): SafeStorage {
  return {
    getItem: (key: string): string | null => {
      if (typeof window === 'undefined') {
        return null;
      }

      try {
        return window[type].getItem(key);
      } catch (error) {
        // Fail silently so we don't crash the app
        console.warn(`[SafeStorage] Blocked reading ${key}`, error);
        return null;
      }
    },

    setItem: (key: string, value: string): void => {
      if (typeof window === 'undefined') {
        return;
      }

      try {
        window[type].setItem(key, value);
      } catch (error) {
        console.warn(`[SafeStorage] Blocked writing ${key}`, error);
      }
    },

    removeItem: (key: string): void => {
      if (typeof window === 'undefined') {
        return;
      }

      try {
        window[type].removeItem(key);
      } catch (error) {
        console.warn(`[SafeStorage] Blocked removing ${key}`, error);
      }
    },

    clear: (): void => {
      if (typeof window === 'undefined') {
        return;
      }

      try {
        window[type].clear();
      } catch (error) {
        console.warn(`[SafeStorage] Blocked clearing storage`, error);
      }
    },
  };
}

export const safeLocalStorage = createSafeStorage('localStorage');
export const safeSessionStorage = createSafeStorage('sessionStorage');
